import lottie from 'lottie-web'
import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import PrismicLink from '@/components/prismic-link'
import {Text} from '@/components/text'
import {BackgroundWrapper, Section} from '@/styles/grid'
import {tokens} from '@/theme'
import mq from '@/utils/media-query'

import type {Props as PrismicLinkProps} from '@/components/prismic-link'

import lottie404Animation from './404-animation.json'

type Props = {
    content: {
        title: string
        body: string
        ctaText: string
        ctaLink: PrismicLinkProps
    }
}

export const NotFound = ({content}: Props) => {
    const animationContainer = React.useRef<HTMLDivElement>(null)
    // we use a mutable ref to avoid triggering re-renders
    const relativeXPosition = React.useRef(0)

    // get the current pointer X position as a number in [0, 1] range
    const getRelativePointerXPosition = (clientX: number) =>
        1 - Math.min(clientX, window.innerWidth) / window.innerWidth

    // Track mouse X position and save it
    React.useEffect(() => {
        const callback = (event: MouseEvent) => {
            relativeXPosition.current = getRelativePointerXPosition(event.clientX)
        }

        document.addEventListener('mousemove', callback, false)

        return () => document.removeEventListener('mousemove', callback)
    }, [])

    // Track touch X position (only on the image) and save it
    React.useEffect(() => {
        const callback = (event: TouchEvent) => {
            if (event.touches.length === 1) {
                relativeXPosition.current = getRelativePointerXPosition(event.touches[0].clientX)
            }
        }

        if (!animationContainer.current) {
            return
        }
        animationContainer.current.addEventListener('touchmove', callback, false)

        return () => document.removeEventListener('touchmove', callback)
    }, [])

    const initialAngle = React.useRef<number | null>(null)

    // Track device orientation and save it
    React.useEffect(() => {
        function handleOrientation(event: DeviceOrientationEvent) {
            const tiltAngle = event.alpha ?? 0

            if (initialAngle.current === null) {
                initialAngle.current = tiltAngle
            }

            const MAX_TILT_LEFT = 45
            const MAX_TILT_RIGHT = -45

            const clumpedTilt = Math.min(
                Math.max(tiltAngle - initialAngle.current, MAX_TILT_RIGHT),
                MAX_TILT_LEFT,
            )

            relativeXPosition.current =
                (clumpedTilt - MAX_TILT_RIGHT) / (MAX_TILT_LEFT - MAX_TILT_RIGHT)
        }

        window.addEventListener('deviceorientation', handleOrientation, true)

        return () => document.removeEventListener('deviceorientation' as any, handleOrientation)
    }, [])

    // Initialize the Lottie animation in a paused state, and save the reference to the animation
    const lottieAnimation = React.useRef<ReturnType<typeof lottie.loadAnimation> | null>(null)

    React.useEffect(() => {
        if (!animationContainer.current) {
            return
        }

        const animation = lottie.loadAnimation({
            container: animationContainer.current,
            renderer: 'svg',
            loop: false,
            autoplay: false,
            animationData: lottie404Animation,
            rendererSettings: {preserveAspectRatio: 'xMidYMin slice'},
        })

        lottieAnimation.current = animation

        return () => animation.destroy()
    }, [])

    // Run requestAnimationFrame loop, on every RAF frame instruct lottie to animate to the
    // animation frame that corresponds to the current X position of the mouse
    const rafId = React.useRef(0)

    React.useEffect(() => {
        const runFrame = () => {
            const animation = lottieAnimation.current

            if (!animation) {
                return
            }

            // currently the animation is back and forth, so we take half the length as max
            const totalFrames = (animation.totalFrames ?? 0) / 2
            const videoFrame = Math.floor(totalFrames * relativeXPosition.current)

            animation.goToAndStop(videoFrame, true)

            // set the raf callback for the next frame
            rafId.current = requestAnimationFrame(runFrame)
        }

        // kick start the raf loop
        rafId.current = requestAnimationFrame(runFrame)

        return () => cancelAnimationFrame(rafId.current)
    }, [])

    return (
        <BackgroundWrapper $color="tertiary">
            <SectionWrapper padding="small">
                <Animation ref={animationContainer} />

                <TextWrapper>
                    <Text $color="inverted" as="h1" $variant="heading-large">
                        {content.title}
                    </Text>

                    <Text $color="inverted" $variant="paragraph-large">
                        {content.body}
                    </Text>

                    <Button $variant="inverted" as={PrismicLink} {...content.ctaLink}>
                        {content.ctaText}
                    </Button>
                </TextWrapper>
            </SectionWrapper>
        </BackgroundWrapper>
    )
}

const SectionWrapper = styled(Section)`
    display: grid;
    padding-top: 0;
    grid-auto-flow: dense;

    ${mq.min('md')} {
        max-width: 1750px;
        min-height: 50vh;
        padding-right: 0;
        grid-template-columns: auto 1fr;
    }

    ${mq.min('lg')} {
        height: 75vh;
    }
`

const Animation = styled.div`
    height: 300px;
    mix-blend-mode: difference;

    ${mq.min('xs')} {
        height: 100%;
    }

    ${mq.min('md')} {
        order: 2;
    }

    svg {
        overflow: visible;
    }
`

const TextWrapper = styled.div`
    display: grid;
    justify-items: start;
    align-self: center;
    gap: ${tokens.spacing20};
`
